import type { components } from '@connected/api-client/api.generated';
import type { ButtonProps } from '@connected/ui/components/button';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { Button } from '@connected/ui/components/button';
import { Popout } from '@connected/ui/components/popout';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useMemo, useState } from 'react';

import { useFormTouched } from '@/hooks/use-form-touched';
import { cn } from '@/lib/utils';
import { showConfirm } from '@/stores/confirmer';

import type { ObservationObjectKinds, ObservationParentKinds, Remark } from '../utils/observation-types';

import { useCreateObservation } from '../hooks/use-observation-mutations';
import { filterReportsByObjectAndParentKind } from '../utils/observation-utils';
import { ObservationModalContent } from './observation-modal-content';

export type CreateRemarkProps<ObjectKind extends ObservationObjectKinds> = ButtonProps & {
    electricalProjectVersionId: string;
    objectId: string;
    objectKind: ObjectKind;
    parentKind: ObservationParentKinds;
    count: number;
};

export function CreateRemark<ObjectKind extends ObservationObjectKinds>({
    electricalProjectVersionId,
    objectId,
    objectKind,
    parentKind,
    count,
    className,
    ...props
}: CreateRemarkProps<ObjectKind>) {
    const [isOpen, setIsModalOpen] = useState(false);

    const [item, setItem] = useState<components['schemas']['ListElectricalProjectObservationQueryResultObservation'] | null>(null);

    const { mutation: createObservationMutation, disabled } = useCreateObservation({ electricalProjectVersionId });
    const { context, inspectionKindsArray, hasInspectionKind } = useElectricalProject();

    // Create initial reports array from unique inspection kinds
    const initialReports = inspectionKindsArray.map(inspectionKind => ({
        inspectionKind,
        enabled: false,
        remarkKind: 'Same' as const,
        remark: null,
        recommendationKind: 'Same' as const,
        recommendation: null,
        electricalObservationClassificationGroupId: null,
        electricalObservationClassificationCodeId: null,
        codeKind: null,
    }));

    const initialReportsFiltered = useMemo(() =>
        filterReportsByObjectAndParentKind(initialReports, objectKind, parentKind), [initialReports, objectKind, parentKind]);

    const defaultValues: Remark = {
        locationRemark: '',
        remark: '',
        recommendation: '',
        electricalObservationClassificationGroupId: '',
        electricalObservationClassificationCodeId: '',
        codeKind: null,
        electricalProjectFiles: [],
        floorplanElectricalProjectFileId: null,
        floorplanPinCoordinateX: null,
        floorplanPinCoordinateY: null,
        reports: initialReportsFiltered,
    };

    const form = useForm<Remark, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
        onSubmit: ({ value }) => {
            createObservationMutation.mutate({
                body: {
                    ElectricalProjectVersionId: electricalProjectVersionId,
                    ElectricalProjectObservationId: item!.ElectricalProjectObservationId!,
                    LocationRemark: value.locationRemark,
                    Remark: value.remark,
                    Recommendation: value.recommendation,
                    ElectricalObservationClassificationGroupId: value.electricalObservationClassificationGroupId,
                    ElectricalObservationClassificationCodeId: value.electricalObservationClassificationCodeId,
                    Order: count + 1,
                    ObjectKind: objectKind,
                    ObjectId: objectId,
                    ElectricalProjectFileIds: value.electricalProjectFiles,
                    FloorplanElectricalProjectFileId: value.floorplanElectricalProjectFileId,
                    FloorplanPinCoordinateX: value.floorplanPinCoordinateX,
                    FloorplanPinCoordinateY: value.floorplanPinCoordinateY,
                    CodeKind: value.codeKind,
                    Reports: value.reports.map(report => ({
                        InspectionKind: report.inspectionKind,
                        Enabled: report.enabled || ((context.selectedInspectionKinds.size === 1 && hasInspectionKind(report.inspectionKind)) || initialReportsFiltered.length === 1), // if there is only one selected inspection kind default it to true
                        RemarkKind: 'Same', // If we implement custom remarks per report, adjust here
                        RecommendationKind: 'Same', // If we implement custom recommendations per report, adjust here
                        ElectricalObservationClassificationGroupId: report.electricalObservationClassificationGroupId || null,
                        ElectricalObservationClassificationCodeId: report.electricalObservationClassificationCodeId || null,
                        CodeKind: report.codeKind || null,
                    })),
                },
            });

            form.reset();
        },
    });
    const isFormTouched = useFormTouched(form.store, defaultValues);

    function onSubmit() {
        form.handleSubmit();
        setIsModalOpen(false);
    }

    async function onClose() {
        if (isFormTouched) {
            if (await showConfirm('Weet je zeker dat je wilt sluiten?')) {
                setIsModalOpen(false);
                form.reset();
            }
        }
        else {
            setIsModalOpen(false);
            form.reset();
        }
    }

    return (
        <Popout open={isOpen}>
            <Button
                {...props}
                className={cn('w-min', className)}
                variant="primary-outline"
                onClick={() => {
                    setItem(null);
                    form.reset();
                    setItem({ ElectricalProjectObservationId: crypto.randomUUID() });
                    setIsModalOpen(true);
                }}
            >
                {/* TODO: translation */}
                Constatering toevoegen
            </Button>
            {item && (
                <ObservationModalContent
                    type="create"
                    electricalProjectVersionId={electricalProjectVersionId}
                    parentKind={parentKind}
                    objectKind={objectKind}
                    form={form}
                    onSubmit={onSubmit}
                    onClose={onClose}
                    disabled={disabled}
                />
            )}
        </Popout>
    );
}
